import React, { useEffect, useState } from "react";
import { styled } from "@mui/system";
import "ol/ol.css"; // Import OpenLayers styles
import { Map, View } from "ol";
import Feature from "ol/Feature";
import Point from "ol/geom/Point";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import { Icon, Style, Text, Fill, Stroke } from "ol/style";
import { Tile as TileLayer } from "ol/layer";
import { OSM, XYZ } from "ol/source";
import { ScaleLine, MousePosition } from "ol/control";
import { toStringHDMS } from "ol/coordinate";
import { defaults as defaultControls, Zoom } from "ol/control";
import { Group as LayerGroup } from "ol/layer.js";
/*-----------------------------*/
import "./MapComponent.css";
import { Material } from "../material/Material";

import { useSelector } from "react-redux";
import { rooApiUrl, workspaceName } from "../../environment/Environment";
import { get as getProjection, transform } from "ol/proj";
import proj4 from "proj4";
import { register } from "ol/proj/proj4";
import Measurment from "../../components/dialogs/measurment-dialog/Measurment";
import { buildCountryLayers } from "../../utilities/layerStorage";
proj4.defs("EPSG:32630", "+proj=utm +zone=30 +datum=WGS84 +units=m +no_defs");
register(proj4);

// Retrieve the projection from OpenLayers
const projection = getProjection("EPSG:32630");
// Custom Popover Style
const CustomPopover = styled(Material.Popover)(({ theme }) => ({
  "& .MuiPaper-root": {
    backgroundColor: theme.palette.background.paper,
    borderRadius: "2px",
    boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
    minWidth: "400px",
    maxWidth: "400px",
    minHeight: 400,
    maxHeight: 400,
    padding: 0,
    transition: "opacity 0.3s ease-in-out", // Smooth transition for the popover
  },
}));

const StyledTabs = styled(Material.Tabs)({
  "& .MuiTabs-indicator": {
    display: "none",
  },
});

const StyledTab = styled(Material.Tab)(({ theme }) => ({
  textTransform: "none",
  color: "#cac3c3",
  backgroundColor: "#ecf2ff",
  borderRadius: "5px 5px 5px 5px",
  padding: 2,
  marginRight: theme.spacing(1),
  "&.Mui-selected": {
    backgroundColor: "#fff",
    color: "#0C141E",
    borderRadius: "5px 5px 5px 5px",
    padding: 2,
    fontWeight: theme.typography.fontWeightBold,
  },
}));
var map;
export default function MapComponent({
  setMapInfo,
  setIsActivefeatureInfo,
  isActivefeatureInfo,
  addNewPointToTheMap,
  allPoints,
  selectedLocation,
  setSelectedLocation,
}) {
  const userDetails = useSelector((state) => state.auth.user);
  const layerDetails = useSelector(
    (state) => state.LayerChangerSlice.layerName
  );
  const alreadySelectedCountryInThestorage = useSelector(
    (state) => state.SelectedCountrySlice.selectedCountry
  );
  const countryDetails = useSelector(
    (state) => state.SelectedCountrySlice?.countryDetails
  );
  const [anchorEl, setAnchorEl] = useState(null);
  const [value, setValue] = useState(0);
  const [selectedBaseMap, setSelectedBaseMap] = useState();
  const [zoomLevel, setZoomLevel] = useState(0);
  const [mousePosition, setMousePosition] = useState("No position");
  const [selectedLayerList, setSelectedLayerList] = useState([]);
  const [activeFeatureInfo, setActiveFeatureInfo] = useState(false);
  const [ledgendUrlList, setLedgendUrlList] = useState([]);
  const [isShowLedgend, setIsShowLedgend] = useState(false);
  const [isMeasure, setIsMeasure] = useState(false);

  const open = Boolean(anchorEl);
  const id = open ? "custom-popover" : undefined;
  const coordinateFormatter = React.useCallback((coord) => {
    const formatted = toStringHDMS(coord, 1);
    setMousePosition(formatted);
    return formatted;
  }, []);

  const mousePositionControl = React.useMemo(() => new MousePosition({
    coordinateFormat: coordinateFormatter,
    projection: "EPSG:4326",
    className: "custom-mouse-position",
    target: null, // We will manage the target manually
  }), [coordinateFormatter]);
  const scaleLineControl = React.useMemo(() => new ScaleLine({
    units: "metric",
    bar: true,
    steps: parseInt(8, 10),
    text: true,
    minWidth: 100,
  }), []);

  const addedPointListForAnalysis = useSelector(
    (state) => state.AddedPointSlice.addedPointList
  );

  useEffect(() => {
    setSelectedBaseMap(
      layerDetails?.baseMapName ? layerDetails.baseMapName : "OSM_LAYER"
    );
    setSelectedLayerList(
      layerDetails?.layerList.length ? layerDetails?.layerList : []
    );
    let layerListforShowingLedgend = [];
    if (layerDetails?.layerList.length > 0) {
      let lr = layerDetails?.layerList.filter(
        (l) => l.type == "raster" || l.originalName == countryDetails.rainfallLayerName || l.originalName == countryDetails.RainfallLayerName
      );
      layerListforShowingLedgend = lr.map((ln) => {
        let obj = {
          layerName: ln.layerName,
          nameWithWorkspace: `${workspaceName}:${ln?.originalName}`,
        };
        return obj;
      });
    }
    //console.log(layerListforShowingLedgend);
    const legendUrls = layerListforShowingLedgend.map((layer) => {
      const obj = {
        ledgendUrl: `${rooApiUrl}/wms?SERVICE=WMS&VERSION=1.1.1&REQUEST=GetLegendGraphic&FORMAT=image/png&WIDTH=50&HEIGHT=50&LAYER=${layer.nameWithWorkspace}&LEGEND_OPTIONS=forceLabels:on;fontSize:50;fontStyle:400;labelMargin:5;dx:30`,
        layerName: layer.layerName,
      };
      return obj;
    });
    setLedgendUrlList(legendUrls);
  }, [layerDetails?.baseMapName, layerDetails?.layerList.length]);

  useEffect(() => {
    // Initialize the map when the component mounts
    console.log('MapComponent useEffect triggered. Country:', alreadySelectedCountryInThestorage);
    if (!alreadySelectedCountryInThestorage || !countryDetails) {
      console.log('No country or countryDetails selected, skipping map initialization');
      return;
    }
    
    // Add a small delay to ensure the DOM is fully rendered and container has dimensions
    let retryCount = 0;
    const maxRetries = 50; // Maximum 5 seconds of retrying
    
    const initializeMap = () => {
      const mapContainer = document.getElementById('map');
      if (!mapContainer) {
        console.log('Map container not found, retrying...');
        if (retryCount < maxRetries) {
          retryCount++;
          setTimeout(initializeMap, 100);
          return;
        } else {
          console.error('Map container never found after maximum retries');
          return;
        }
      }
      
      // Check if container has proper dimensions
      const containerRect = mapContainer.getBoundingClientRect();
      console.log('Map container dimensions:', containerRect.width, 'x', containerRect.height);
      
      // If we have width but no height, the flexbox layout might not be fully calculated yet
      // But let's try to initialize anyway after some retries, as OpenLayers can handle height: 0 in some cases
      if (containerRect.width === 0) {
        console.log('Map container has zero width, retrying...');
        if (retryCount < maxRetries) {
          retryCount++;
          setTimeout(initializeMap, 100);
          return;
        } else {
          console.warn('Proceeding with map initialization despite zero width after max retries');
        }
      } else if (containerRect.height === 0) {
        console.log('Map container has zero height, but width is available. Retrying a few more times...');
        if (retryCount < maxRetries) {
          retryCount++;
          setTimeout(initializeMap, 100);
          return;
        } else {
          console.warn('Proceeding with map initialization despite zero height - flexbox should expand');
        }
      }
      
      console.log('Initializing map with container dimensions:', containerRect.width, 'x', containerRect.height);
      
      map = new Map({
        target: "map", // The id of the element to render the map in
        layers: [
          new TileLayer({
            visible: selectedBaseMap === "OSM_LAYER",
            source: new OSM({
              crossOrigin: "Anonymous", // Allow exporting map image
            }),
          }),
          new TileLayer({
            visible: selectedBaseMap === "TOPO_LAYER",
            source: new XYZ({
              attributions:
                'Tiles © <a href="https://services.arcgisonline.com/ArcGIS/' +
                'rest/services/World_Topo_Map/MapServer">ArcGIS</a>',
              url:
                "https://server.arcgisonline.com/ArcGIS/rest/services/" +
                "World_Topo_Map/MapServer/tile/{z}/{y}/{x}",
              crossOrigin: "Anonymous",
            }),
          }),
          new TileLayer({
            visible: selectedBaseMap === "WORLD_IMAGINERY_LAYER",
            source: new XYZ({
              url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
              crossOrigin: "Anonymous",
            }),
          }),
        ],
        //interactions: defaultInteractions({ dragPan: false }), //this is for fixed in the particular location
        view: new View({
          projection: "EPSG:3857",
          center: [
            countryDetails.mapCenterLongitude ?? countryDetails.MapCenterLongitude ?? 0,
            countryDetails.mapCenterLatitude ?? countryDetails.MapCenterLatitude ?? 0
          ],
          // The center of the map in [longitude, latitude] in EPSG:3857 projection
          zoom: countryDetails.mapZoomLevel ?? countryDetails.MapZoomLevel ?? 6, // Initial zoom level
        }),
        controls: defaultControls({
          zoom: false, // Disable the default zoom control
          // other controls can be customized here
        }),
      });
      
      map.addControl(scaleLineControl);
      map.addControl(mousePositionControl);
      let layerGroup = new LayerGroup({
        layers: selectedLayerList.map((l) => l.value),
      });

      map.addLayer(layerGroup);
      map.getView().on("change:resolution", () => {
        setZoomLevel(map.getView().getZoom().toFixed(2));
      });

      // Attach map to the window for easy access in event handlers
      window.map = map;
      //setMapInfo(map);

      // Call function to add all points to map
      if (allPoints && allPoints.length > 0) {
        const pointListBySelectedCountry = allPoints.filter(
          (point) => point.countryName === alreadySelectedCountryInThestorage
        );
        addAllVectorLayerTotheMap(pointListBySelectedCountry);
      }

      if (selectedLocation !== null) {
        goToSelectedLocation();
      }
      
      // Force map to update its size after initialization
      setTimeout(() => {
        if (map) {
          map.updateSize();
          console.log('Map size updated after initialization');
        }
      }, 100);
      
      // Only set map info if it's different to prevent infinite loops
      if (setMapInfo && map) {
        setMapInfo(map);
        console.log('Map successfully initialized and setMapInfo called');
      } else {
        console.log('setMapInfo not called - setMapInfo:', !!setMapInfo, 'map:', !!map);
      }
    };
    
    // Start the initialization process
    initializeMap();
    
    // Clean up on unmount
    return () => {
      if (map) {
        map.dispose();
      }
    };
  }, [
    alreadySelectedCountryInThestorage,
    selectedBaseMap,
    selectedLayerList?.length,
    allPoints?.length,
    countryDetails,
  ]);

  useEffect(() => {
    setIsActivefeatureInfo(activeFeatureInfo);
  }, [activeFeatureInfo]);
  // Zoom In and Zoom Out Handlers
  const zoomIn = () => {
    const view = window.map.getView();
    view.setZoom(view.getZoom() + 1);
  };
  const goToSelectedLocation = () => {
    const featureExtent = selectedLocation.getGeometry().getExtent();
    map?.getView().fit(featureExtent, { duration: 1000, maxZoom: 15 });
    setSelectedLocation(null);
  };
  const zoomOut = () => {
    const view = window.map.getView();
    view.setZoom(view.getZoom() - 1);
  };

  const handleGotoHome = () => {
    if (map) {
      map.getView().animate({
        center: [
          countryDetails.mapCenterLongitude ?? countryDetails.MapCenterLongitude ?? 0,
          countryDetails.mapCenterLatitude ?? countryDetails.MapCenterLatitude ?? 0
        ], // The center of the map in [longitude, latitude] in EPSG:3857 projection
        zoom: countryDetails.mapZoomLevel ?? countryDetails.MapZoomLevel ?? 6,
        duration: 1000, // Animation duration in milliseconds
      });
    }
  };

  const showLedgend = () => {
    setIsShowLedgend(!isShowLedgend);
    setIsMeasure(false);
  };

  const handleMeasurement = () => {
    setIsMeasure(!isMeasure);
    setIsShowLedgend(false);
  };

  const newPointAdd = () => {
    addNewPointToTheMap();
  };

  const addAllVectorLayerTotheMap = (points) => {
    points.forEach((point) => {
      // Create a feature for each point
      const feature = new Feature({
        geometry: new Point(point.mapPointArray),
        name: point.pointName,
      });

      feature.setStyle(
        new Style({
          image: new Icon({
            src: "https://cdn-icons-png.flaticon.com/512/684/684908.png",
            scale: 0.1, // Increase the icon size
            anchor: [0.5, 1], // Bottom center anchor
            color: "#fd0303", // Tint the icon to a specific color (requires PNG with alpha)
            //rotation: Math.PI / 4, // Rotate 45 degrees (in radians)
            opacity: 1, // Set transparency
            //displacement: [10, 0], // Shift icon by 10px horizontally
            crossOrigin: "anonymous",
          }),
          text: new Text({
            text: point.pointName,
            font: "bold 13px Poppins",
            fill: new Fill({ color: "#f70000" }),
            stroke: new Stroke({ color: "#ffffff", width: 3 }),
          }),
        })
      );

      const vectorSource = new VectorSource({
        features: [feature],
      });

      const vectorLayer = new VectorLayer({
        source: vectorSource,
        zIndex: 2,
      });
      vectorLayer.set("name", point.pointName);
      vectorLayer.setVisible(true);
      map.addLayer(vectorLayer);
    });
  };

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
      }}
      className="px-0"
    >
      <div id="map" style={{ width: "100%", height: "100%", minHeight: "400px" }}></div>

      {/* Custom Zoom and Move Buttons */}

      <div
        className="row p-2"
        style={{
          position: "absolute",
          top: "109px",
          maxWidth: 300,
          backgroundColor: "white",
          border: 1,
          borderStyle: "solid",
          left: "60px",
          boxShadow:
            "rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 2px 6px 2px",
        }}
        hidden={!isMeasure}
      >
        <Measurment mapInstance={map} isMeasure={isMeasure} />
      </div>
      <div
        style={{
          position: "absolute",
          minWidth: "200px",
          maxWidth: "300px",
          height: 300,
          transition: "width 0.5s ease, height 0.5s ease, opacity 0.5s ease", // Apply transitions for width, height, and opacity
          // maxHeight: "500px",
          // zIndex: 2000,
          top: "109px",
          left: "45px",
          backgroundColor: "white",
          padding: 5,
          //border: 2,
          //borderStyle: "solid",
          //borderColor: "#54d1f7",
          borderRadius: 5,
          overflowY: "auto",
          overflowX: "hidden",
          transition: "width 4s ease-in-out",
          boxShadow:
            "rgba(0, 0, 0, 0.25) 0px 0.0625em 0.0625em, rgba(0, 0, 0, 0.25) 0px 0.125em 0.5em, rgba(255, 255, 255, 0.1) 0px 0px 0px 1px inset",
        }}
        hidden={!isShowLedgend}
      >
        {ledgendUrlList?.length > 0 ? (
          ledgendUrlList?.map((l, index) => (
            <div
              key={index}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                marginBottom: "10px",
              }}
            >
              <span
                className="p-1 "
                style={{ fontSize: "0.6rem", fontWeight: "bold" }}
              >{`${l?.layerName} Legend`}</span>
              <img
                src={l?.ledgendUrl}
                alt={""}
                style={{
                  width: "auto",
                  height: "auto",
                  maxWidth: "150px",
                  maxHeight: "150px",
                  objectFit: "contain",
                  marginTop: 3,
                  marginLeft: 5,
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                }}
              />
            </div>
          ))
        ) : (
          <div
            className="p-1 d-flex align-items-center justify-content-between w-100 border border-2 border-warning rounded-2"
            style={{
              fontSize: ".800rem",
              color: "#fdfbfb",
              backgroundColor: "#ff5e00",
              fontWeight: "bold",
            }}
          >
            <i className="fa-solid fa-triangle-exclamation fa-bounce p-2 fs-2"></i>
            <span>
              {" "}
              No legend is present as you have not added any raster layer from
              the layer management menu
            </span>
          </div>
        )}
      </div>
      {/* <div
        id="mouse-position"
        style={{
          position: "absolute",
          bottom: 55,
          left: 10,
          fontSize: 10,
          color: "#0C141E",
          backgroundColor: "rgba(255, 255, 255, 0.7)",
          padding: "5px",
          borderRadius: "5px",
        }}
      >
        <div className="fw-bold">Zoom Level: {zoomLevel}</div>
        <div className="fw-bold">Coordinates: {mousePosition}</div>
      </div> */}
    </div>
  );
}
